/* oxlint-disable typescript/no-explicit-any -- schema library requires flexible any types */
/* oxlint-disable typescript/no-unsafe-argument, typescript/no-unsafe-assignment, typescript/no-unsafe-call, typescript/no-unsafe-member-access, typescript/no-unsafe-return -- schema library uses any types throughout */
import { BaseSchema } from '../core/base-schema'
import type { OptionalSchema, NullableSchema } from '../core/base-schema'
import * as errors from '../core/errors'
import * as types from '../core/types'
import * as util from '../core/util'

// Record schema definition
export interface RecordSchemaDef<K extends types.Schema, V extends types.Schema>
  extends types.SchemaTypeDef {
  type: 'record'
  keySchema: K
  valueSchema: V
}

export interface RecordSchemaInternals<
  K extends types.Schema,
  V extends types.Schema,
> extends types.SchemaInternals<
  Record<Extract<types.output<K>, PropertyKey>, types.output<V>>,
  Record<Extract<types.input<K>, PropertyKey>, types.input<V>>
> {
  def: RecordSchemaDef<K, V>
}

export interface RecordSchema<
  K extends types.Schema,
  V extends types.Schema,
> extends types.Schema<
  Record<Extract<types.output<K>, PropertyKey>, types.output<V>>,
  Record<Extract<types.input<K>, PropertyKey>, types.input<V>>,
  RecordSchemaInternals<K, V>
> {
  meta(metadata: Record<string, any>): this
  optional(): OptionalSchema<this>
  nullable(): NullableSchema<this>
  parse(
    data: unknown,
    params?: types.ParseContext,
  ): Record<Extract<types.output<K>, PropertyKey>, types.output<V>>
  safeParse(
    data: unknown,
    params?: types.ParseContext,
  ):
    | {
        success: true
        data: Record<Extract<types.output<K>, PropertyKey>, types.output<V>>
      }
    | { success: false; error: errors.SchemaError }
}

export const RecordSchema = types.$constructor<RecordSchema<any, any>>(
  'RecordSchema',
  (inst, def: RecordSchemaDef<any, any>) => {
    BaseSchema.init(inst, def)

    // Adapted from pinned Zod 4.5.4 $ZodRecord; schema definitions stay local.
    inst._zod.parse = (payload, ctx) => {
      const input = payload.value as Record<PropertyKey, unknown>
      if (typeof input !== 'object' || input === null || Array.isArray(input) || (Object.getPrototypeOf(input) !== null && Object.getPrototypeOf(input).constructor !== Object)) {
        payload.issues.push({ code: 'invalid_type', expected: 'record', input, inst })
        return payload
      }
      const result: Record<PropertyKey, unknown> = {}
      const promises: Promise<void>[] = []
      const keyDef = def.keySchema._zod.def
      const finiteKeys: PropertyKey[] | undefined = keyDef.type === 'enum' ? keyDef.values : keyDef.type === 'literal' ? [keyDef.value] : undefined
      const keys = finiteKeys ?? Reflect.ownKeys(input).filter(key => Object.prototype.propertyIsEnumerable.call(input, key))
      for (const key of keys) {
        if (key === '__proto__') continue
        let kp = def.keySchema._zod.run({ value: key, issues: [] }, ctx)
        if (kp instanceof Promise) throw new Error('Async schemas not supported in object keys currently')
        if (kp.issues.length > 0 && typeof key === 'string' && key !== '' && !Number.isNaN(Number(key))) {
          const retry = def.keySchema._zod.run({ value: Number(key), issues: [] }, ctx)
          if (retry instanceof Promise) throw new Error('Async schemas not supported in object keys currently')
          if (retry.issues.length === 0) kp = retry
        }
        if (kp.issues.length > 0) {
          payload.issues.push({ code: 'invalid_key', origin: 'record', issues: kp.issues.map((issue: errors.RawIssue) => errors.formatIssue(issue, ctx.error)), input: key, path: [key], inst })
          continue
        }
        const outputKey = kp.value as PropertyKey
        if (outputKey === '__proto__') continue
        const parsed = def.valueSchema._zod.run({ value: input[key], issues: [] }, ctx)
        const finish = (p: types.ParsePayload) => {
          if (p.issues.length > 0) payload.issues.push(...util.prefixIssues(key, p.issues))
          result[outputKey] = p.value
        }
        if (parsed instanceof Promise) promises.push(parsed.then(finish))
        else finish(parsed)
      }
      if (finiteKeys) {
        const recognized = new Set(finiteKeys.map(key => typeof key === 'number' ? String(key) : key))
        const extra: string[] = []
        for (const key in input) if (!recognized.has(key)) extra.push(key)
        if (extra.length > 0) payload.issues.push({ code: 'unrecognized_keys', keys: extra, input, inst, continue: true })
      }
      if (promises.length > 0) return Promise.all(promises).then(() => { payload.value = result; return payload })
      payload.value = result
      return payload
    }
  },
)

export function record<K extends types.Schema, V extends types.Schema>(
  keySchema: K,
  valueSchema: V,
): RecordSchema<K, V> {
  return new RecordSchema({
    type: 'record',
    keySchema,
    valueSchema,
  })
}
